import { useAutocompleteStyles } from '@app/src/components/Form/Select/SimpleSelect'
import TextField from '@app/src/components/Ui/TextField'
import usePagination from '@app/src/hooks/pagination'
import type { CountryResult } from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import {
  useSearchCountries,
  useTabularImportRecords,
  useUpdateTabularRecord,
} from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import type { ImportRecord } from '@app/src/types/tabularImport'
import {
  extractedFieldsAreValid,
  getMissingRequiredFields,
  hasRequiredFields,
  parseExtractedData,
} from '@app/src/types/tabularImport'
import { Check, CheckCircle, Close, Edit, Error, Pending, Search, Visibility, Warning } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useDebounce } from 'use-debounce'
import type { FieldConfig } from '../fieldConfig'
import { useTabularImportEntity } from '../TabularImportEntityContext'
import { MappingStatusEditor, ProductStatusEditor, ProviderEditor } from './EntityEditors'
import RowDetailModal from './RowDetailModal'

export type ExtraRecordColumn = {
  header: string
  render: (record: ImportRecord) => React.ReactNode
}

interface EditableRecordsTableProps {
  sessionId: string
  totalRows: number
  successfulRows: number
  failedRows: number
  extraColumns?: ExtraRecordColumn[]
}

type StatusFilter = 'all' | 'Success' | 'Failed' | 'Pending'

const SEARCH_DEBOUNCE_MS = 500

const stickyActionCellSx = (zIndex: number, bgcolor: string) => ({
  position: 'sticky' as const,
  right: 0,
  zIndex,
  bgcolor,
  borderLeft: '1px solid',
  borderColor: 'divider',
})

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'Success':
      return <CheckCircle fontSize="small" color="success" />
    case 'Failed':
      return <Error fontSize="small" color="error" />
    default:
      return <Pending fontSize="small" color="action" />
  }
}

const getStatusColor = (status: string): 'success' | 'error' | 'default' => {
  switch (status) {
    case 'Success':
      return 'success'
    case 'Failed':
      return 'error'
    default:
      return 'default'
  }
}

// Palette has no `.lighter` tint — `.light` is the pale shade. The wrong token resolved to
// an invalid (transparent) colour, which also made the sticky Actions cell see-through so
// the previous column bled under its buttons.
function getRowBackground(record: ImportRecord, hasRequired: boolean): string | undefined {
  if (record.isManuallyEdited) return 'info.light'
  if (record.status === 'Failed') return 'error.light'
  if (!hasRequired) return 'warning.light'
  return undefined
}

function getExtractedFields(record: ImportRecord): Record<string, string> {
  const extracted = parseExtractedData(record.extractedData)
  if (!extracted?.extractedFields) return {}

  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(extracted.extractedFields)) {
    if (value) result[key] = value
  }
  return result
}

// Render a field cell in read mode
function ReadCell({ fieldConfig, fields }: { fieldConfig: FieldConfig; fields: Record<string, string | null> }) {
  const value = fields[fieldConfig.key]

  if (fieldConfig.required) {
    return (
      <Typography variant="body2" color={value ? 'text.primary' : 'error.main'} fontWeight={!value ? 'bold' : 'normal'}>
        {value || 'Missing *'}
      </Typography>
    )
  }

  if (fieldConfig.truncate) {
    return (
      <Typography
        variant="body2"
        color={value ? 'text.primary' : 'text.secondary'}
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {value || '-'}
      </Typography>
    )
  }

  return (
    <Typography variant="body2" color="text.secondary">
      {value || '-'}
    </Typography>
  )
}

// Render a field cell in edit mode
function EditCell({
  fieldConfig,
  editValues,
  isSaving,
  onFieldChange,
  setEditResolvedIds,
  countryProps,
}: {
  fieldConfig: FieldConfig
  editValues: Record<string, string>
  isSaving: boolean
  onFieldChange: (field: string, value: string) => void
  setEditResolvedIds: React.Dispatch<React.SetStateAction<Record<string, number | null>>>
  countryProps: {
    countryResults: CountryResult[]
    countrySearch: string
    setCountrySearch: (v: string) => void
    selectedCountry: CountryResult | null
    setSelectedCountry: (v: CountryResult | null) => void
  }
}) {
  const autocompleteClasses = useAutocompleteStyles()
  const value = editValues[fieldConfig.key] || ''

  if (fieldConfig.readOnly) {
    return (
      <Typography variant="body2" color="text.secondary">
        {value || '-'}
      </Typography>
    )
  }

  if (fieldConfig.editType === 'country') {
    const { countryResults, countrySearch, setCountrySearch, selectedCountry, setSelectedCountry } = countryProps
    return (
      <Autocomplete
        size="small"
        freeSolo
        options={countryResults}
        getOptionLabel={(option: string | CountryResult) => (typeof option === 'string' ? option : option.name)}
        value={selectedCountry}
        inputValue={countrySearch}
        onInputChange={(_, v, reason) => {
          setCountrySearch(v)
          onFieldChange('Country', v)
          // Typed text is not a country until it is picked from the list. Keeping the previously
          // resolved id would save the new name against the old country, because UpdateRecord
          // merges omitted ids with the ones already on the record.
          if (reason === 'input') {
            setSelectedCountry(null)
            setEditResolvedIds(prev => ({ ...prev, CountryId: null }))
          }
        }}
        onChange={(_, v) => {
          if (v && typeof v !== 'string') {
            setSelectedCountry(v)
            onFieldChange('Country', v.name)
            setEditResolvedIds(prev => ({ ...prev, CountryId: v.id }))
          } else {
            setSelectedCountry(null)
            setEditResolvedIds(prev => ({ ...prev, CountryId: null }))
          }
        }}
        disabled={isSaving}
        classes={{
          clearIndicator: autocompleteClasses.clearIndicator,
          popupIndicator: autocompleteClasses.popupIndicator,
          paper: autocompleteClasses.paper,
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            name="countryEditor"
            required={fieldConfig.required}
            error={fieldConfig.required && !value}
            helperText={fieldConfig.required && !value ? 'Required' : ''}
          />
        )}
        sx={{ minWidth: 150 }}
      />
    )
  }

  // The resolved-id key `<Field>Id` matches what the backend import job writes.
  if (fieldConfig.editType === 'provider') {
    return (
      <ProviderEditor
        value={value}
        disabled={isSaving}
        required={fieldConfig.required}
        onChange={(display, providerId) => {
          onFieldChange(fieldConfig.key, display)
          setEditResolvedIds(prev => ({ ...prev, [`${fieldConfig.key}Id`]: providerId }))
        }}
      />
    )
  }

  if (fieldConfig.editType === 'productStatus') {
    return <ProductStatusEditor value={value} disabled={isSaving} onChange={v => onFieldChange(fieldConfig.key, v)} />
  }

  if (fieldConfig.editType === 'mappingStatus') {
    return <MappingStatusEditor value={value} disabled={isSaving} onChange={v => onFieldChange(fieldConfig.key, v)} />
  }

  return (
    <TextField
      variant="outlined"
      name={fieldConfig.key}
      size="small"
      value={value}
      onChange={e => onFieldChange(fieldConfig.key, e.target.value)}
      disabled={isSaving}
      fullWidth
      required={fieldConfig.required}
      error={fieldConfig.required && !value}
      helperText={fieldConfig.required && !value ? 'Required' : ''}
    />
  )
}

const EditableRecordsTable: React.FC<EditableRecordsTableProps> = ({
  sessionId,
  totalRows,
  successfulRows,
  failedRows,
  extraColumns = [],
}) => {
  const { fieldConfigs, requiredFields } = useTabularImportEntity()
  const [page, pageSize, setPage, setPageSize] = usePagination()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch] = useDebounce(searchInput, SEARCH_DEBOUNCE_MS)
  const [editingRow, setEditingRow] = useState<number | null>(null)
  const [editValues, setEditValues] = useState<Record<string, string>>({})
  const [editResolvedIds, setEditResolvedIds] = useState<Record<string, number | null>>({})
  const [savingRow, setSavingRow] = useState<number | null>(null)
  const [selectedRecord, setSelectedRecord] = useState<ImportRecord | null>(null)
  const [countrySearch, setCountrySearch] = useState('')
  const [debouncedCountrySearch] = useDebounce(countrySearch, SEARCH_DEBOUNCE_MS)
  const [selectedCountry, setSelectedCountry] = useState<CountryResult | null>(null)

  const apiStatusFilter = statusFilter === 'all' ? undefined : statusFilter
  const { data: recordsData, isLoading } = useTabularImportRecords(
    sessionId,
    page,
    pageSize,
    apiStatusFilter,
    debouncedSearch || undefined,
  )

  const { mutateAsync: updateRecord, isPending: isUpdatingRecord } = useUpdateTabularRecord()
  const { items: countryResults } = useSearchCountries(debouncedCountrySearch, editingRow !== null)

  const records = recordsData?.items ?? []
  const totalCount = recordsData?.totalResult ?? 0

  const pendingRows = totalRows - successfulRows - failedRows
  const statusCounts = {
    all: totalRows,
    Success: successfulRows,
    Failed: failedRows,
    Pending: pendingRows,
  }

  const handleStatusFilterChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newFilter: StatusFilter | null) => {
      if (newFilter) {
        setStatusFilter(newFilter)
        setPage(1)
      }
    },
    [setPage],
  )

  const handleStartEdit = useCallback((record: ImportRecord) => {
    setEditingRow(record.rowNumber)
    const fields = getExtractedFields(record)
    setEditValues(fields)
    setEditResolvedIds({})
    setCountrySearch(fields.Country || '')
    setSelectedCountry(null)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingRow(null)
    setEditValues({})
    setEditResolvedIds({})
    setSelectedCountry(null)
    setCountrySearch('')
  }, [])

  const handleSaveEdit = useCallback(
    async (rowNumber: number) => {
      setSavingRow(rowNumber)
      try {
        const resolvedIds = Object.keys(editResolvedIds).length > 0 ? editResolvedIds : undefined
        await updateRecord({ sessionId, rowNumber, extractedFields: editValues, resolvedIds })
        setEditingRow(null)
        setEditValues({})
        setEditResolvedIds({})
        setSelectedCountry(null)
        setCountrySearch('')
      } finally {
        setSavingRow(null)
      }
    },
    [editValues, editResolvedIds, sessionId, updateRecord],
  )

  const handleEditFieldChange = useCallback((field: string, value: string) => {
    setEditValues(prev => ({ ...prev, [field]: value }))
  }, [])

  const countryProps = {
    countryResults,
    countrySearch,
    setCountrySearch,
    selectedCountry,
    setSelectedCountry,
  }

  // Total columns: Row + Status + fields + extra columns + Errors + Actions
  const totalColumns = fieldConfigs.length + 4 + extraColumns.length

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
        <ToggleButtonGroup value={statusFilter} exclusive onChange={handleStatusFilterChange} size="small">
          <ToggleButton value="all">All ({statusCounts.all})</ToggleButton>
          <ToggleButton value="Success" color="success">
            Success ({statusCounts.Success})
          </ToggleButton>
          <ToggleButton value="Failed" color="error">
            Failed ({statusCounts.Failed})
          </ToggleButton>
          {statusCounts.Pending > 0 && <ToggleButton value="Pending">Pending ({statusCounts.Pending})</ToggleButton>}
        </ToggleButtonGroup>

        <TextField
          variant="outlined"
          name="recordSearch"
          size="small"
          placeholder="Search records..."
          value={searchInput}
          onChange={e => {
            setSearchInput(e.target.value)
            setPage(1)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 250 }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
        <Table size="small" sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Status</TableCell>
              {fieldConfigs.map(fc => (
                <TableCell key={fc.key}>{fc.label}</TableCell>
              ))}
              {extraColumns.map(col => (
                <TableCell key={col.header}>{col.header}</TableCell>
              ))}
              <TableCell>Errors</TableCell>
              <TableCell align="right" sx={stickyActionCellSx(3, 'background.paper')}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map(record => {
              const isEditing = editingRow === record.rowNumber
              const isSaving = savingRow === record.rowNumber
              const extracted = parseExtractedData(record.extractedData)
              const fields = extracted?.extractedFields || {}
              const hasErrors = record.validationErrors.length > 0

              const missingFields = getMissingRequiredFields(record, requiredFields)
              const recordHasRequiredFields = hasRequiredFields(record, requiredFields)

              return (
                <TableRow
                  key={record.rowNumber}
                  sx={{
                    bgcolor: getRowBackground(record, recordHasRequiredFields),
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" fontFamily="monospace">
                        #{record.rowNumber}
                      </Typography>
                      {record.isManuallyEdited && (
                        <Tooltip title={`Edited ${record.editedAt ? new Date(record.editedAt).toLocaleString() : ''}`}>
                          <Edit fontSize="inherit" color="info" sx={{ fontSize: 12 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {!recordHasRequiredFields ? (
                      <Tooltip title={`Missing required fields: ${missingFields.join(', ')}`}>
                        <Chip
                          icon={<Warning fontSize="small" />}
                          label="Incomplete"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        icon={getStatusIcon(record.status)}
                        label={record.status}
                        size="small"
                        color={getStatusColor(record.status)}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  {fieldConfigs.map(fc => (
                    <TableCell key={fc.key}>
                      {isEditing ? (
                        <EditCell
                          fieldConfig={fc}
                          editValues={editValues}
                          isSaving={isSaving}
                          onFieldChange={handleEditFieldChange}
                          setEditResolvedIds={setEditResolvedIds}
                          countryProps={countryProps}
                        />
                      ) : (
                        <ReadCell fieldConfig={fc} fields={fields} />
                      )}
                    </TableCell>
                  ))}
                  {extraColumns.map(col => (
                    <TableCell key={col.header}>{col.render(record)}</TableCell>
                  ))}
                  <TableCell>
                    {hasErrors ? (
                      <Typography variant="body2" color="error.main" noWrap>
                        <Tooltip title={record.validationErrors.join(', ')}>
                          <span>{record.validationErrors.join(', ')}</span>
                        </Tooltip>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={stickyActionCellSx(1, getRowBackground(record, recordHasRequiredFields) ?? 'background.paper')}
                  >
                    {isEditing ? (
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveEdit(record.rowNumber)}
                          disabled={isSaving || !extractedFieldsAreValid(editValues, requiredFields)}
                        >
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit} disabled={isSaving}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(record)}
                          disabled={isUpdatingRecord || editingRow !== null}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setSelectedRecord(record)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {records.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center">
                  <Typography color="text.secondary" py={2}>
                    {debouncedSearch ? 'No matching records found' : 'No records to display'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 250]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={page - 1}
          onPageChange={(_, newPage) => {
            setPage(newPage + 1)
            setEditingRow(null)
          }}
          onRowsPerPageChange={event => {
            setPageSize(parseInt(event.target.value, 10))
            setPage(1)
            setEditingRow(null)
          }}
        />
      </TableContainer>

      <RowDetailModal record={selectedRecord} open={!!selectedRecord} onClose={() => setSelectedRecord(null)} />
    </Box>
  )
}

export default EditableRecordsTable
